import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   useColorModeValue,
   ModalHeader,
   ModalCloseButton,
   ModalBody,
   ModalFooter,
   Flex,
   Box,
   Button,
   Table,
   Thead,
   Tbody,
   Tr,
   Th,
   Td,
   TableContainer,
   Tfoot,
} from '@chakra-ui/react';
import InputField from 'components/ui/InputField';

const SelectTakaDetail = ({
   isOpen,
   onClose,
   onCloseComplete,
   takaDetails = [],
   handleSaveTakaDetail,
   selectedTakaDetails = [],
}) => {
   const [rows, setRows] = useState([]);
   const inputRefs = useRef({});
   const saveBtnRefs = useRef({});

   const takaDetailsWithIds = useMemo(() => {
      return takaDetails.map((item) => ({
         ...item,
         id: item.id,
      }));
   }, [takaDetails]);

   const rowTotal = useMemo(() => {
      const usedMeter = rows.reduce((acc, item) => {
         return acc + Number(item.usedMeter);
      }, 0);

      const mts = rows.reduce((acc, item) => {
         return acc + Number(item.mts);
      }, 0);

      const shortMts = rows.reduce((acc, item) => {
         return acc + Number(item.shortMts);
      }, 0);
      const shortPerc = rows
         .reduce((acc, item) => {
            return acc + Number(item.shortPerc);
         }, 0)
         .toFixed(2);

      return { usedMeter, mts, shortMts, shortPerc };
   }, [rows]);

   const handleOnClose = useCallback(() => {
      onClose();
   }, [onClose]);

   const handleOnChange = useCallback(
      (e, idx) => {
         const { name, value } = e.target;
         const updatedRows = [...rows];
         updatedRows[idx][name] = Number(value);
         updatedRows[idx].shortMts = Number(updatedRows[idx].usedMeter ?? 0) - Number(value);
         updatedRows[idx].shortPerc = (
            (updatedRows[idx].shortMts / Number(updatedRows[idx].usedMeter ?? 0)) *
            100
         ).toFixed(2);
         setRows(updatedRows);
      },
      [rows],
   );

   const getNextInput = useCallback(
      (currentRowIdx, currentField) => {
         const fields = ['mts', 'remarks'];
         const currentFieldIdx = fields.indexOf(currentField);

         if (currentFieldIdx < fields.length - 1) {
            return {
               rowIdx: currentRowIdx,
               field: fields[currentFieldIdx + 1],
            };
         }

         if (currentRowIdx < rows.length - 1) {
            return {
               rowIdx: currentRowIdx + 1,
               field: fields[0],
            };
         }

         return null;
      },
      [rows.length],
   );

   const getPreviousInput = useCallback((currentRowIdx, currentField) => {
      const fields = ['mts', 'remarks'];
      const currentFieldIdx = fields.indexOf(currentField);

      if (currentFieldIdx > 0) {
         return {
            rowIdx: currentRowIdx,
            field: fields[currentFieldIdx - 1],
         };
      }

      if (currentRowIdx > 0) {
         return {
            rowIdx: currentRowIdx - 1,
            field: fields[fields.length - 1],
         };
      }

      return null;
   }, []);

   const handleKeyDown = useCallback(
      (e, rowIdx, fieldName) => {
         if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();

            let nextInput;
            if (e.shiftKey && e.key === 'Tab') {
               nextInput = getPreviousInput(rowIdx, fieldName);
            } else {
               nextInput = getNextInput(rowIdx, fieldName);
            }

            if (nextInput) {
               const inputKey = `${nextInput.rowIdx}-${nextInput.field}`;
               const inputElement = inputRefs.current[inputKey];
               if (inputElement) {
                  inputElement.focus();
                  inputElement.select();
               }
            } else {
               if (saveBtnRefs.current) saveBtnRefs.current.focus();
            }
         }
      },
      [getNextInput, getPreviousInput],
   );

   const handleSave = useCallback(() => {
      handleSaveTakaDetail(rows);
      handleOnClose();
      setRows([]);
   }, [handleOnClose, handleSaveTakaDetail, rows]);

   useEffect(() => {
      const rowData = takaDetailsWithIds.map((item) => {
         return {
            id: item.id,
            usedMeter: item.issued_qty3_taka_meters,
            mts: '',
            remarks: '',
            shortMts: 0,
            shortPerc: 0,
            issueLineDetailId: item.issue_line_detail_id,
         };
      });

      setRows(rowData);
      return () => {};
   }, [takaDetailsWithIds]);

   useEffect(() => {
      if (selectedTakaDetails.length > 0) {
         setRows(selectedTakaDetails);
      }
      return () => {};
   }, [selectedTakaDetails]);

   useEffect(() => {
      if (isOpen && rows.length > 0) {
         const timer = setTimeout(() => {
            const firstMtsInput = inputRefs.current['0-mts'];
            if (firstMtsInput) {
               firstMtsInput.focus();
               // firstMtsInput.select();
            }
         }, 100);

         return () => clearTimeout(timer);
      }

      return () => {};
   }, [isOpen, rows.length]);

   return (
      <Modal isOpen={isOpen} onClose={handleOnClose} isCentered onCloseComplete={onCloseComplete} size="3xl">
         <ModalOverlay />
         <ModalContent
            bg={useColorModeValue('white', '#2d3748')}
            color={useColorModeValue('gray.800', 'white')}
            borderRadius="20px"
            p={5}
            boxShadow="lg">
            <ModalHeader>
               <Flex justifyContent="space-between" alignItems="center">
                  <Box>Item Detail</Box>
               </Flex>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
               <Box maxH="400px" overflowY="auto">
                  <TableContainer border="1px" borderColor="gray.200" borderRadius={5}>
                     <Table variant="simple" border="1px solid #E2E8F0" size="md" width="100%">
                        <Thead>
                           <Tr>
                              <Th isNumeric minW="100px">
                                 Used Meter
                              </Th>
                              <Th isNumeric minW="100px">
                                 MTS
                              </Th>
                              <Th minW="150px">Remarks</Th>
                              <Th isNumeric minW="100px">
                                 Short.MTS
                              </Th>
                              <Th isNumeric minW="100px">
                                 Short.%
                              </Th>
                           </Tr>
                        </Thead>
                        <Tbody>
                           {rows.map((item, idx) => {
                              return (
                                 <Tr key={item.id}>
                                    <Td isNumeric>
                                       <InputField
                                          ref={(el) => (inputRefs.current[`${idx}-usedMeter`] = el)}
                                          type="number"
                                          name="usedMeter"
                                          size="sm"
                                          onWheel={(e) => e.target.blur()}
                                          textAlign="right"
                                          value={item.usedMeter}
                                          onChange={(e) => handleOnChange(e, idx)}
                                          disabled
                                       />
                                    </Td>
                                    <Td isNumeric>
                                       <InputField
                                          ref={(el) => (inputRefs.current[`${idx}-mts`] = el)}
                                          type="number"
                                          name="mts"
                                          size="sm"
                                          onWheel={(e) => e.target.blur()}
                                          textAlign="right"
                                          value={item.mts}
                                          onChange={(e) => handleOnChange(e, idx)}
                                          onKeyDown={(e) => handleKeyDown(e, idx, 'mts')}
                                       />
                                    </Td>
                                    <Td>
                                       <InputField
                                          ref={(el) => (inputRefs.current[`${idx}-remarks`] = el)}
                                          type="text"
                                          name="remarks"
                                          size="sm"
                                          value={item.remarks}
                                          onChange={(e) => handleOnChange(e, idx)}
                                          onKeyDown={(e) => handleKeyDown(e, idx, 'remarks')}
                                       />
                                    </Td>
                                    <Td isNumeric>
                                       <InputField
                                          type="number"
                                          name="shortMts"
                                          size="sm"
                                          onWheel={(e) => e.target.blur()}
                                          textAlign="right"
                                          value={item.shortMts}
                                          onChange={(e) => handleOnChange(e, idx)}
                                          disabled
                                       />
                                    </Td>
                                    <Td isNumeric>
                                       <InputField
                                          type="number"
                                          name="shortPerc"
                                          size="sm"
                                          onWheel={(e) => e.target.blur()}
                                          textAlign="right"
                                          value={item.shortPerc}
                                          onChange={(e) => handleOnChange(e, idx)}
                                          disabled
                                       />
                                    </Td>
                                 </Tr>
                              );
                           })}
                        </Tbody>
                        <Tfoot>
                           <Tr>
                              <Th isNumeric>{rowTotal.usedMeter}</Th>
                              <Th isNumeric>{rowTotal.mts}</Th>
                              <Th>-</Th>
                              <Th isNumeric>{rowTotal.shortMts}</Th>
                              <Th isNumeric>{rowTotal.shortPerc}</Th>
                           </Tr>
                        </Tfoot>
                     </Table>
                  </TableContainer>
               </Box>
            </ModalBody>
            <ModalFooter>
               <Flex>
                  <Button
                     ref={saveBtnRefs}
                     colorScheme="blue"
                     ml={3}
                     width="100%"
                     _focus={{ outlineColor: 'blue.500' }}
                     onClick={handleSave}>
                     Save
                  </Button>
                  <Button colorScheme="red" onClick={() => handleOnClose()} ml={3} width="100%">
                     Cancel
                  </Button>
               </Flex>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
};

export default SelectTakaDetail;
